import type {ItemLoadingOverlay} from '@PosterWhiteboard/items/item/item.types';
import {AnimatedItemLoadingOverlayVariant, ItemLoadingOverlayType, StaticItemLoadingOverlayVariant} from '@PosterWhiteboard/items/item/item.types';

interface GradientState {
  from: [number, number];
  to: [number, number];
  colors: string[];
}

const C_BLUE = 'rgb(105, 227, 227)';
const C_PINK = 'rgb(221, 49, 255)';
const C_PURPLE = 'rgb(89, 54, 255)';

const GRADIENT_STATES: GradientState[] = [
  {from: [-1, -1], to: [1, 1], colors: [C_BLUE, C_PURPLE, C_PINK]},
  {from: [1, -1], to: [-1, 1], colors: [C_PURPLE, C_PINK, C_BLUE]},
  {from: [0, -1], to: [0, 1], colors: [C_PINK, C_PURPLE, C_BLUE]},
];

const LIGHT_GRAY_OVERLAY = 'rgb(200, 200, 200)';

/** How strongly the overlay covers the item (1 = opaque). Applied absolutely, independent of the item's own opacity. */
const OVERLAY_ALPHA = 0.6;

/** Paints the overlay fill into a buffer already centred on the item's origin. */
type OverlayFill = (ctx: CanvasRenderingContext2D, width: number, height: number) => void;

const fillState = (ctx: CanvasRenderingContext2D, width: number, height: number, state: GradientState): void => {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const gradient = ctx.createLinearGradient(state.from[0] * halfWidth, state.from[1] * halfHeight, state.to[0] * halfWidth, state.to[1] * halfHeight);
  state.colors.forEach((color, index) => {
    gradient.addColorStop(index / (state.colors.length - 1), color);
  });
  ctx.fillStyle = gradient;
  ctx.fillRect(-halfWidth, -halfHeight, width, height);
};

/** Cross-fades through {@link GRADIENT_STATES} cyclically as `phase` goes 0→1 (seamless loop). */
const drawMorphGradient = (ctx: CanvasRenderingContext2D, width: number, height: number, phase: number): void => {
  const count = GRADIENT_STATES.length;
  const scaled = phase * count;
  const currentState = GRADIENT_STATES[Math.floor(scaled) % count];
  const nextState = GRADIENT_STATES[(Math.floor(scaled) + 1) % count];
  if (!currentState || !nextState) {
    return;
  }
  const localFade = scaled - Math.floor(scaled);

  ctx.save();
  fillState(ctx, width, height, currentState);
  ctx.globalAlpha = localFade;
  fillState(ctx, width, height, nextState);
  ctx.restore();
};

const fillLightGray: OverlayFill = (ctx, width, height) => {
  ctx.fillStyle = LIGHT_GRAY_OVERLAY;
  ctx.fillRect(-width / 2, -height / 2, width, height);
};

const STATIC_OVERLAY_FILLS: Record<StaticItemLoadingOverlayVariant, OverlayFill> = {
  [StaticItemLoadingOverlayVariant.LIGHT_GRAY]: fillLightGray,
};

const ANIMATED_OVERLAY_FILLS: Record<AnimatedItemLoadingOverlayVariant, (phase: number) => OverlayFill> = {
  [AnimatedItemLoadingOverlayVariant.GRADIENT]: (phase) => (ctx, width, height) => {
    drawMorphGradient(ctx, width, height, phase);
  },
};

const getOverlayFill = (overlay: ItemLoadingOverlay, phase: number): OverlayFill => {
  if (overlay.type === ItemLoadingOverlayType.ANIMATED) {
    return ANIMATED_OVERLAY_FILLS[overlay.variant](phase);
  }

  return STATIC_OVERLAY_FILLS[overlay.variant];
};

interface OverlayBufferGeometry {
  scaleX: number;
  scaleY: number;
  bufferWidth: number;
  bufferHeight: number;
}

let itemCanvas: HTMLCanvasElement | null = null;
let overlayCanvas: HTMLCanvasElement | null = null;

const ensureCanvas = (canvas: HTMLCanvasElement | null, width: number, height: number): HTMLCanvasElement => {
  const result = canvas ?? document.createElement('canvas');
  if (result.width !== width || result.height !== height) {
    result.width = width;
    result.height = height;
  }
  return result;
};

/** Resets a buffer context to a clean opaque state and re-centres the origin on the item's middle, ready for a fresh draw. */
const prepBufferContext = (ctx: CanvasRenderingContext2D, {scaleX, scaleY, bufferWidth, bufferHeight}: OverlayBufferGeometry): void => {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, bufferWidth, bufferHeight);
  ctx.setTransform(scaleX, 0, 0, scaleY, bufferWidth / 2, bufferHeight / 2);
};

export const paintItemLoadingOverlay = (
  targetCtx: CanvasRenderingContext2D,
  renderItem: (ctx: CanvasRenderingContext2D) => void,
  width: number,
  height: number,
  overlay: ItemLoadingOverlay,
  phase: number
): void => {
  const paintFill = getOverlayFill(overlay, phase);

  const transform = targetCtx.getTransform();
  const scaleX = Math.hypot(transform.a, transform.b) || 1;
  const scaleY = Math.hypot(transform.c, transform.d) || 1;
  const bufferWidth = Math.max(1, Math.ceil(width * scaleX));
  const bufferHeight = Math.max(1, Math.ceil(height * scaleY));

  itemCanvas = ensureCanvas(itemCanvas, bufferWidth, bufferHeight);
  overlayCanvas = ensureCanvas(overlayCanvas, bufferWidth, bufferHeight);
  const itemCtx = itemCanvas.getContext('2d');
  const overlayCtx = overlayCanvas.getContext('2d');
  if (!itemCtx || !overlayCtx) {
    return;
  }

  const geometry: OverlayBufferGeometry = {scaleX, scaleY, bufferWidth, bufferHeight};

  // The item silhouette, isolated at full alpha — used only as the mask for the overlay.
  prepBufferContext(itemCtx, geometry);
  renderItem(itemCtx);

  // The overlay fill, built opaque.
  prepBufferContext(overlayCtx, geometry);
  paintFill(overlayCtx, width, height);

  // Keep the overlay only where the item is opaque, so itemCanvas now holds the shape-masked overlay alone.
  itemCtx.setTransform(1, 0, 0, 1, 0, 0);
  itemCtx.globalCompositeOperation = 'source-in';
  itemCtx.globalAlpha = 1;
  itemCtx.drawImage(overlayCanvas, 0, 0);

  // Draw the real item at its own (inherited) opacity, then the masked overlay at a fixed alpha on top.
  targetCtx.save();
  renderItem(targetCtx);
  targetCtx.restore();

  targetCtx.save();
  targetCtx.globalAlpha = OVERLAY_ALPHA;
  targetCtx.drawImage(itemCanvas, -width / 2, -height / 2, width, height);
  targetCtx.restore();
};
